<template>
  <div>
    <div class="row items-center q-mb-xs">
      <div class="text-subtitle2">Eye Settings</div>
      <q-space />
      <q-select
        v-model="localIrisStyle"
        :options="irisStyleOptions"
        option-label="label"
        option-value="value"
        emit-value
        map-options
        dense
        borderless
        popup-content-class="windowed-popup"
        style="min-width: 140px"
      />
    </div>

    <!-- Eye Preview -->
    <div class="row justify-center q-mt-sm q-mb-sm">
      <div class="col-auto">
        <svg width="180" height="120" viewBox="0 0 180 120" role="img" aria-label="Eye preview">
          <defs>
            <radialGradient :id="scleraGradId" cx="50%" cy="50%" r="50%">
              <stop offset="0%" :stop-color="local.scleraCenter || '#F6F9FC'" />
              <stop offset="100%" :stop-color="local.scleraEdge || '#D7DDE6'" />
            </radialGradient>
          </defs>
          <!-- Sclera gradient with subtle edge stroke -->
          <circle :cx="90" :cy="60" :r="40" :fill="'url(#' + scleraGradId + ')'"></circle>
          <circle
            :cx="90"
            :cy="60"
            :r="42"
            fill="none"
            :stroke="local.scleraEdge || '#D7DDE6'"
            stroke-width="1.5"
          ></circle>
          <!-- Iris border ring -->
          <circle :cx="90" :cy="60" :r="24" :fill="local.irisBorderColor || '#22303A'" />
          <!-- Iris -->
          <circle :cx="90" :cy="60" :r="20" :fill="local.irisColor || '#6B4423'" />
          <!-- Pupil -->
          <circle :cx="90" :cy="60" :r="9" :fill="local.pupilColor || '#0A0E16'" />
          <!-- Highlight -->
          <circle :cx="82" :cy="52" :r="3" fill="#ffffff" fill-opacity="0.85" />
        </svg>
      </div>
    </div>
    <div class="q-mt-sm">
      <div class="text-caption q-mb-xs">Iris</div>
      <div class="row q-col-gutter-sm">
        <div class="col-12 col-sm-6 col-md-4">
          <ColorPickerInput
            v-model:text-value="local.irisText"
            v-model:color-value="local.irisColor"
            label="Iris Color"
            icon="visibility"
            :show-icon="false"
            swatch-position="prepend"
            color-type="eyes"
            field-name="eye.iris"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4">
          <ColorPickerInput
            v-model:text-value="local.irisBorderText"
            v-model:color-value="local.irisBorderColor"
            label="Iris Border"
            icon="radio_button_unchecked"
            :show-icon="false"
            swatch-position="prepend"
            color-type="eyes"
            field-name="eye.irisBorder"
          />
        </div>
      </div>
    </div>

    <div class="q-mt-sm">
      <div class="text-caption q-mb-xs">Pupil</div>
      <div class="row q-col-gutter-sm">
        <div class="col-12 col-sm-6 col-md-4">
          <ColorPickerInput
            v-model:text-value="local.pupilText"
            v-model:color-value="local.pupilColor"
            label="Pupil Color"
            icon="lens"
            :show-icon="false"
            swatch-position="prepend"
            color-type="eyes"
            field-name="eye.pupil"
          />
        </div>
      </div>
    </div>

    <div class="q-mt-sm">
      <div class="text-caption q-mb-xs">Sclera (White)</div>
      <div class="row q-col-gutter-sm">
        <div class="col-12 col-sm-6 col-md-4">
          <ColorPickerInput
            v-model:text-value="local.scleraCenterText"
            v-model:color-value="local.scleraCenter"
            label="Center"
            icon="brightness_5"
            :show-icon="false"
            swatch-position="prepend"
            color-type="eyes"
            field-name="eye.scleraCenter"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4">
          <ColorPickerInput
            v-model:text-value="local.scleraEdgeText"
            v-model:color-value="local.scleraEdge"
            label="Edge"
            icon="brightness_6"
            :show-icon="false"
            swatch-position="prepend"
            color-type="eyes"
            field-name="eye.scleraEdge"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, ref, watch, nextTick } from 'vue'
  import ColorPickerInput from '@/components/ui/form/ColorPickerInput.vue'

  const defaults = {
    // Provide sensible defaults so fields are never empty when dialog opens
    irisColor: '#6B4423',
    irisBorderColor: '#22303A',
    pupilColor: '#0A0E16',
    scleraCenter: '#F6F9FC',
    scleraEdge: '#D7DDE6',
    irisText: '',
    irisBorderText: '',
    pupilText: '',
    scleraCenterText: '',
    scleraEdgeText: ''
  }

  const withDefaults = (src = {}) => {
    const out = { ...defaults }
    const s = src || {}
    const sanitize = (val, defVal) => {
      if (val === undefined || val === null) return defVal
      if (typeof defVal === 'string') {
        const t = String(val).trim()
        return t ? t : defVal
      }
      return val
    }
    for (const k of Object.keys(out)) out[k] = sanitize(s[k], out[k])
    return out
  }

  export default defineComponent({
    name: 'EyesSettings',
    components: { ColorPickerInput },
    props: {
      eye: { type: Object, default: () => ({}) },
      irisStyle: { type: String, default: 'vector' }
    },
    emits: ['update:eye', 'update:irisStyle'],
    setup(props, { emit }) {
      const local = ref(withDefaults(props.eye))
      const isSyncingFromProps = ref(false)
      const localIrisStyle = ref(props.irisStyle || 'vector')

      watch(
        () => props.eye,
        (n) => {
          // Guard against feedback loops: update fields without emitting back
          isSyncingFromProps.value = true
          const merged = withDefaults(n)
          // Preserve object identity to avoid resetting child inputs during drags
          Object.assign(local.value, merged)
          nextTick(() => {
            isSyncingFromProps.value = false
          })
        },
        { deep: true }
      )

      watch(
        () => local.value,
        (n) => {
          if (isSyncingFromProps.value) return
          emit('update:eye', withDefaults(n))
        },
        { deep: true }
      )

      const irisStyleOptions = [
        { label: 'Vector', value: 'vector' },
        { label: 'Image', value: 'image' }
      ]

      watch(
        () => props.irisStyle,
        (n) => (localIrisStyle.value = n || 'vector')
      )

      watch(
        () => localIrisStyle.value,
        (n) => emit('update:irisStyle', n)
      )

      // Unique id for gradient to avoid DOM id collisions
      const scleraGradId = 'scleraGrad-' + Math.random().toString(36).slice(2, 8)

      return { local, localIrisStyle, irisStyleOptions, scleraGradId }
    }
  })
</script>

<style scoped></style>
